import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { i18next, view, cssGenerator, dialogs, textFormatting } from '@yola/ws-sdk';
import highlighter from 'src/js/modules/highlighter';
import TextCustomSpacingDialog from '../components/text-custom-spacing-dialog';
import getElementSpacingValues from '../helpers/get-element-spacing-values';
import truncateToOneDecimal from '../helpers/truncate-to-one-decimal';
import restoreRange from '../helpers/restore-range';
import trackTextEditingCustomSpacingDialogDisplayed from '../trackers/track-text-editing-custom-spacing-dialog-displayed';

const { MARGIN_TOP, MARGIN_BOTTOM, LINE_HEIGHT } = cssGenerator.constants.attributes;

const fieldNames = {
  LINE_HEIGHT: 'lineHeight',
  MARGIN_TOP: 'marginTop',
  MARGIN_BOTTOM: 'marginBottom',
};

const fieldLimits = {
  [fieldNames.LINE_HEIGHT]: { min: 0, max: 10 },
  [fieldNames.MARGIN_TOP]: { min: 0, max: 999 },
  [fieldNames.MARGIN_BOTTOM]: { min: 0, max: 999 },
};

const DEBOUNCE_DELAY = 300;

const getCaptions = () => ({
  title: i18next.t('Custom spacing'),
  submitButton: i18next.t('Submit'),
  cancelButton: i18next.t('Cancel'),
  lineHeight: i18next.t('Line height'),
  paragraphSpacing: i18next.t('Paragraph spacing'),
  top: i18next.t('Top'),
  bottom: i18next.t('Bottom'),
});

const attributeHandlers = {
  [fieldNames.LINE_HEIGHT]: (element, value) => {
    if (isFinite(value)) {
      const oldValue = element.getAttribute(LINE_HEIGHT) || '';
      const newValue = view.helpers.createResponsiveValue(value.toString(), oldValue, 'xl');
      element.setAttribute(LINE_HEIGHT, newValue);
    } else {
      element.removeAttribute(LINE_HEIGHT);
    }
  },
  [fieldNames.MARGIN_TOP]: (element, value) => {
    if (isFinite(value)) {
      element.setAttribute(MARGIN_TOP, value.toString());
    } else {
      element.removeAttribute(MARGIN_TOP);
    }
  },
  [fieldNames.MARGIN_BOTTOM]: (element, value) => {
    if (isFinite(value)) {
      element.setAttribute(MARGIN_BOTTOM, value.toString());
    } else {
      element.removeAttribute(MARGIN_BOTTOM);
    }
  },
};

const TextCustomSpacingContainer = ({ focusedElements, onDialogMainAction, onDialogCancel }) => {
  const captions = getCaptions();
  const dispatch = useDispatch();
  const firstElement = focusedElements[0];
  const closestEditableElement = firstElement.closest('[contenteditable]');
  const elementId = view.accessors.getLiveElementId(closestEditableElement);

  const initialValues = useMemo(() => getElementSpacingValues(firstElement), [firstElement]);
  const [state, setState] = useState(initialValues);

  const updateElementStyles = useCallback(
    (fieldName, value) => {
      const handler = attributeHandlers[fieldName];
      if (handler) {
        focusedElements.forEach((element) => handler(element, value));
      }
    },
    [focusedElements]
  );

  const generateAndUpdateCss = useCallback(async () => {
    const liveDocument = view.accessors.getLiveDocument();
    const css = await cssGenerator.operations.generateCssFromHtml(
      liveDocument.documentElement.innerHTML
    );
    dispatch(view.actions.updateAtomicCSS(css));
  }, [dispatch]);

  const debouncedGenerateAndUpdateCss = useMemo(
    () =>
      debounce(() => {
        generateAndUpdateCss();
      }, DEBOUNCE_DELAY),
    [generateAndUpdateCss]
  );

  const restoreElement = () => {
    const selection = textFormatting.accessors.getAdvancedSelection();
    restoreRange(selection, elementId);
  };

  const handleChange = useCallback(
    (fieldName, value) => {
      const numericValue = Number(value);

      const { min, max } = fieldLimits[fieldName];
      let clampedValue = Math.max(min, Math.min(max, numericValue));

      if (fieldName === fieldNames.LINE_HEIGHT) {
        clampedValue = truncateToOneDecimal(clampedValue);
      }

      setState((prev) => ({
        ...prev,
        [fieldName]: clampedValue,
      }));

      updateElementStyles(fieldName, clampedValue);
      debouncedGenerateAndUpdateCss();

      highlighter.operations.show([closestEditableElement], {
        forceUpdate: true,
        labelGap: 'medium',
      });
    },
    [updateElementStyles, debouncedGenerateAndUpdateCss, closestEditableElement]
  );

  const handleSubmit = () => {
    onDialogMainAction({
      targetElement: closestEditableElement,
      topParagraphSpacing: state.marginTop.toString(),
      bottomParagraphSpacing: state.marginBottom.toString(),
      customLineHeight: state.lineHeight.toString(),
    });

    dialogs.operations.hide();
    restoreElement();
  };

  const handleCancel = async () => {
    Object.entries(initialValues).forEach(([fieldName, value]) => {
      updateElementStyles(fieldName, value);
    });

    await generateAndUpdateCss();

    onDialogCancel({
      targetElement: closestEditableElement,
    });

    dialogs.operations.hide();
    restoreElement();
  };

  useEffect(
    () => () => {
      debouncedGenerateAndUpdateCss.cancel();
    },
    [debouncedGenerateAndUpdateCss]
  );

  useEffect(() => {
    trackTextEditingCustomSpacingDialogDisplayed({
      targetElement: closestEditableElement,
      topParagraphSpacing: state.marginTop.toString(),
      bottomParagraphSpacing: state.marginBottom.toString(),
      customLineHeight: state.lineHeight.toString(),
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <TextCustomSpacingDialog
      state={state}
      captions={captions}
      onLineHeightChange={(value) => handleChange(fieldNames.LINE_HEIGHT, value)}
      onMarginTopChange={(value) => handleChange(fieldNames.MARGIN_TOP, value)}
      onMarginBottomChange={(value) => handleChange(fieldNames.MARGIN_BOTTOM, value)}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

TextCustomSpacingContainer.propTypes = {
  focusedElements: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default TextCustomSpacingContainer;
